/**
 * Asking a robot to DO something. The pure half.
 *
 * This is the only surface in the app that MOVES A MACHINE, so it is deliberately the
 * most cautious thing in it. Three independent gates, each catching a different mistake:
 *
 *   1. the controls are DISARMED until explicitly armed, and re-disarm on their own, so
 *      a panel left open is not a panel left loaded;
 *   2. every action needs a SECOND, DIFFERENT click that names the robot and the act;
 *   3. an action the robot does not currently permit cannot be pressed at all.
 *
 * None of these replaces the adapter's own checks — Keenon refuses out-of-state commands
 * with a receipt code regardless, and `status/permitted` is derived from that same
 * matrix. They exist so a refusal is something the operator reads BEFORE acting rather
 * than an error afterwards.
 *
 * WHY A COMMAND IS NOT A REQUEST/RESPONSE. `cmd/action` is a HIGH-LEVEL operation: it can
 * be refused, and a refusal is indistinguishable from "hasn't happened yet" by watching
 * telemetry. So it carries a `command_id` and its outcome arrives separately on
 * `telemetry/command`. Nothing here waits for a reply; the UI watches for the event.
 */
import type { Capability, CommandEvent, Robot } from "./fleet";
import type { ParamSchema } from "./debugQuery";

/**
 * WHETHER A COMMAND IS ACTUALLY SENT.
 *
 * TRUE AS OF 2026-09-21. This surface now moves a real robot, deliberately and with the
 * operator's authorisation, for supervised testing with people present.
 *
 * What the harness proved before the switch: both gates behave, the permitted check
 * follows `status/permitted`, and the composed payloads are correct against the vendor's
 * real schema — checked by pressing every command and reading the topic and body that
 * would have gone out.
 *
 * WHAT CHANGES WITH IT ON, beyond the obvious:
 *
 *   * the permitted gate becomes binding again (`canPress`). While false, a command the
 *     robot would refuse could still be composed, so the payload could be checked
 *     against a robot that was never in the right state. That leniency ENDS here: the
 *     interface being used must be the interface that ships.
 *   * the banner turns red and says LIVE.
 *
 * Still a constant and not a UI toggle: a control that can arm the real thing is a
 * control somebody can arm by accident. Turning it back off is the same one-line change
 * and redeploy, which is the right amount of friction in both directions.
 */
export const COMMANDS_ENABLED = true;

/** One command that WOULD have been sent. Kept separate from `telemetry/command` events
 *  rather than mixed in: a simulated entry is not something the robot said, and a log
 *  that blurs the two is worse than no log. */
/**
 * One command this panel tried to issue — whether it went out or not.
 *
 * KEPT WHEN LIVE, not only in the harness. What was actually published is the thing
 * an operator needs beside the robot's response: `telemetry/command` says a command was
 * rejected, and only this says which command, with which arguments, on which topic. A
 * refusal whose request you cannot see is a refusal you cannot act on.
 */
export interface CommandAttempt {
  at: number;
  name: string;
  topic: string;
  payload: unknown;
  /** True when it was published. False means the harness composed it and stopped. */
  sent: boolean;
  /** Set when the robot would NOT have accepted this. Only reachable in the harness,
   *  where a gated command can still be composed; empty when live. */
  wouldRefuse: string;
}

/**
 * Whether a control can be pressed at all.
 *
 * THE GATE DIFFERS BY MODE, deliberately, and this is the one place that knows it.
 *
 * Live, an operation the robot does not currently permit is unpressable: the interface
 * being used must be the interface that ships, and offering a button that earns a vendor
 * refusal teaches people to ignore greyed controls.
 *
 * In the HARNESS the opposite is true. The whole purpose is to exercise what WOULD be
 * sent, and `clean` is accepted only from idle — so a robot that is charging, cleaning or
 * docked makes the one command worth testing untestable. So it is pressable, and the
 * record it produces says the robot would have refused and why. That keeps both facts:
 * the payload is right, AND the robot would not have taken it.
 *
 * Missing arguments block in BOTH modes. That is a form gate, not a robot gate — there
 * is no payload to compose without them.
 */
export function canPress(view: ActionView, values: Record<string, string>,
                         armed: boolean, live: boolean): boolean {
  if (!armed) return false;
  if (missingArgs(view, values).length) return false;
  return live ? view.state === "ready" : true;
}

/** How long an armed control stays armed with no further interaction. A panel on a wall
 *  must not still be dangerous twenty minutes after somebody walked away. */
export const ARM_TIMEOUT_MS = 60_000;

/** How long a confirmation stands before it must be asked again. Short: this is the gap
 *  between "I meant to press that" and "I pressed it a while ago and forgot". */
export const CONFIRM_TIMEOUT_MS = 10_000;

export type ActionState = "ready" | "not-now" | "unavailable";

export interface ActionView {
  name: string;
  title: string;
  state: ActionState;
  /** Why it cannot be pressed. Always set when state is not `ready`. */
  reason: string;
  /** When this would be accepted, in a sentence. Shown WITH the refusal, because
   *  "not allowed in the robot's current state" answers a question nobody asked. */
  when: string;
  /** A caveat the operator must see before pressing — an unverified shape, a vendor
   *  quirk. An operation with one that looks identical to one without is how a warning
   *  goes unread. */
  notes: string;
  /** JSON Schema per parameter, straight from `status/capabilities`. Empty for an
   *  operation that takes none. THE ONLY source of what an operation accepts — a second
   *  list in this app would be a second thing to keep true. */
  params: Record<string, ParamSchema>;
  required: string[];
}

/**
 * What the operator may do, and what to say when they may not.
 *
 * The THREE-WAY distinction is the whole point and is carried end to end from the
 * contract: `unavailable` is "not on this robot" (permanent, a property of the machine),
 * `not-now` is "not in this state" (temporary, it will come back). Collapsing them into
 * one greyed button loses the only information the operator needs to decide whether to
 * wait or to give up.
 */
export function actionViews(robot: Robot | undefined): ActionView[] {
  const caps = robot?.capabilities ?? [];
  return caps.map((c) => ({
    name: c.name,
    title: c.title || c.name,
    when: c.when ?? "",
    notes: c.notes ?? "",
    params: (c.params ?? {}) as Record<string, ParamSchema>,
    // Everything a capability declares is required unless it carries a default. The
    // adapter enforces exactly that (`command._params_for`), so the two agree by
    // construction rather than by two lists being kept in step.
    required: Object.entries(c.params ?? {})
      .filter(([, sc]) => !(sc as ParamSchema | undefined)?.hasOwnProperty("default"))
      .map(([k]) => k),
    ...resolve(c, robot?.permitted),
  }));
}

function resolve(c: Capability, permitted: string[] | undefined):
    { state: ActionState; reason: string } {
  if (c.enabled === false) {
    return { state: "unavailable", reason: c.reason || "not available on this robot" };
  }
  if (permitted === undefined) {
    // NEVER PUBLISHED is not the same as "permits nothing". A robot that has said
    // nothing about its state should not have its controls silently greyed as though it
    // had refused — say which it is.
    return { state: "not-now", reason: "this robot has not reported what it will accept" };
  }
  if (!permitted.includes(c.name)) {
    // The sentence the adapter publishes, when it has one. "Not allowed in the robot's
    // current state" is true and useless — an operator can see the control is greyed;
    // what they need is what would ungrey it.
    return { state: "not-now",
             reason: c.when || "not allowed in the robot's current state" };
  }
  return { state: "ready", reason: "" };
}

/** Unique per press. Correlates the action with its outcome on `telemetry/command`,
 *  which is the only place a refusal appears — MQTT 5 response topics are unavailable
 *  end to end, so correlation is by id in the payload. */
export function newCommandId(): string {
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The outcome of one command, if it has arrived yet. */
export function outcomeFor(robot: Robot | undefined, commandId: string):
    CommandEvent | undefined {
  return (robot?.commands ?? []).find((e) => e.command_id === commandId);
}

/** A terminal phase — the command is finished, one way or the other. */
export function isTerminal(phase: string): boolean {
  return phase === "complete" || phase === "failed" || phase === "rejected";
}

// ---- the arming/confirmation state machine -----------------------------------
//
// Kept here, pure and clock-injected, because "is this control live right now" is
// exactly the logic that must not be scattered through render code.

export interface ArmState {
  /** When the controls were armed, ms, or 0 for disarmed. */
  armedAt: number;
  /** The action awaiting a confirming second click, and when it was requested. */
  pending?: { name: string; at: number };
}

export const disarmed = (): ArmState => ({ armedAt: 0 });

export function isArmed(s: ArmState, now: number): boolean {
  return s.armedAt > 0 && now - s.armedAt < ARM_TIMEOUT_MS;
}

export function isConfirming(s: ArmState, name: string, now: number): boolean {
  return !!s.pending && s.pending.name === name &&
         now - s.pending.at < CONFIRM_TIMEOUT_MS;
}

/**
 * One press. Returns the next state and whether to actually SEND.
 *
 * The first press on an action only arms that action; the second sends it. A press on a
 * DIFFERENT action replaces the pending one rather than sending either — so a misclick
 * while a confirmation is up cannot fire the thing you just aimed away from.
 */
/**
 * Form values, typed as the operation's schema declares them.
 *
 * A FORM PRODUCES STRINGS. The contract carries typed scalars, and the adapter validates
 * against the same schema this reads — so an uncoerced `"6"` where an integer is
 * declared is refused before it ever reaches the vendor. That happened on the first live
 * command: `backPointId must be integer, got str`.
 *
 * The query surface has done this since it was built (`debugQuery.coerce`); the command
 * path was written later and did not, which is the whole bug. Both now convert at the
 * edge, where the strings actually are.
 *
 * A BLANK IS DROPPED rather than sent empty, matching the adapter's own rule: it treats
 * an absent value as "use the default", and an empty string as a value that fails its
 * type. `cleanTimes` left alone must mean 1, not "".
 */
export function coerceArgs(view: ActionView,
                           values: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, schema] of Object.entries(view.params)) {
    const raw = String(values[name] ?? "").trim();
    if (raw === "") continue;
    const t = schema?.type;
    if (t === "integer" || t === "number") {
      const n = Number(raw);
      // A non-number is passed through UNCHANGED rather than becoming NaN: the adapter's
      // message about a type it can name is more use than one about NaN.
      out[name] = Number.isFinite(n) ? n : raw;
    } else if (t === "boolean") {
      out[name] = raw === "true" || raw === "1" || raw === "on";
    } else {
      out[name] = raw;
    }
  }
  return out;
}

/** What is still missing before an operation is worth sending. Empty = go. */
export function missingArgs(v: ActionView, values: Record<string, string>): string[] {
  return v.required.filter((n) => !String(values[n] ?? "").trim());
}

export function press(s: ArmState, name: string, now: number):
    { next: ArmState; send: boolean } {
  if (!isArmed(s, now)) return { next: s, send: false };
  if (isConfirming(s, name, now)) {
    // Consumed. Disarming entirely after a send is deliberate: one deliberate act per
    // arming, so a run of clicks cannot become a run of commands.
    return { next: disarmed(), send: true };
  }
  return { next: { armedAt: s.armedAt, pending: { name, at: now } }, send: false };
}
